"use client"

import { ExternalLink } from "lucide-react"
import { contentAnalytics } from "@/lib/analytics"
import { merchantLabelForHref } from "@/lib/affiliate-cta"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"

interface AffiliateCardCTAProps {
  href: string
  name: string
  slug: string
  asin: string | null
}

// Shared surface for the in-content "Recommended products" card (AffiliateCardBlock).
// Used by BOTH the impression and the click so affiliate_product_shown /
// affiliate_product_clicked join and per-surface CTR is computable. These
// card-v1 anchors are deliberately skipped by the blog prose observer/handler
// (blog-content-with-toc.tsx), so the card owns its own analytics here.
const CARD_V1_SURFACE = 'blog-card-v1'

export function AffiliateCardCTA({ href, name, slug, asin }: AffiliateCardCTAProps) {
  // Fires affiliate_product_shown once when the card is 50% visible. Attaching
  // to the CTA anchor is sufficient — the card above it is on screen by then.
  const impressionRef = useAffiliateImpression(CARD_V1_SURFACE, {
    article_slug: slug,
    product_name: name,
    affiliate_link: href,
    asin: asin ?? undefined,
    placement: 'card-v1',
  })

  const handleClick = () => {
    contentAnalytics.affiliateLinkClicked(slug, name, href, 'card-v1', asin)
    // Unified click on the SAME surface as the impression above, alongside the
    // legacy event, so card-v1 CTR becomes computable without breaking history.
    contentAnalytics.affiliateProductClicked(CARD_V1_SURFACE, {
      productName: name,
      affiliateLink: href,
      asin: asin ?? undefined,
      article_slug: slug,
      placement: 'card-v1',
    })
  }

  return (
    <a
      ref={impressionRef}
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      data-affiliate="true"
      data-affiliate-placement="card-v1"
      onClick={handleClick}
      className="flex w-full items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white! hover:text-white! text-xs font-semibold px-3 py-2 rounded-md transition-colors no-underline hover:no-underline whitespace-nowrap"
    >
      Check price on {merchantLabelForHref(href)}
      <ExternalLink className="w-3 h-3" />
    </a>
  )
}
